import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageUpload } from "@/components/ui/image-upload";
import { Calendar as CalendarIcon, Loader2, Plus, X, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

const invoiceSchema = z.object({
  invoice_number: z.string().min(1, "Invoice number is required"),
  title: z.string().min(1, "Invoice title is required"),
  client_id: z.string().min(1, "Please select a client"),
  project_id: z.string().optional(),
  po_type: z.string().optional(),
  po_number: z.string().optional(),
  issue_date: z.date(),
  due_date: z.date(),
  status: z.enum(["paid", "not_paid"]),
  discount_percentage: z.number().min(0).max(100).optional(),
  pdf_url: z.string().optional(),
  products: z.array(z.object({
    product_id: z.string().min(1, "Please select a product"),
    list_price: z.number().min(0, "List price must be positive"),
    invoiced_price: z.number().min(0, "Invoiced price must be positive"),
  })).min(1, "At least one product is required"),
}).refine((data) => {
  // Ensure due_date is after issue_date
  return data.due_date > data.issue_date;
}, {
  message: "Due date must be after issue date",
  path: ["due_date"],
});

interface Client {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  client_id: string;
}

interface ProductOption {
  id: string;
  name: string;
  base_price?: number | null;
}

interface InvoiceFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: z.infer<typeof invoiceSchema> & { currency: string; amount: number }) => void;
  clients: Client[];
  projects: Project[];
  products: ProductOption[];
  isSubmitting?: boolean;
  editingInvoice?: {
    id: string;
    title?: string;
    invoice_number?: string;
    client_id: string;
    project_id?: string;
    issue_date?: string;
    due_date?: string;
    status?: string;
    amount: number;
    currency?: string;
    po_number?: string;
    po_type?: string;
    discount_percentage?: number;
    pdf_url?: string;
    invoice_items?: Array<{
      id: string;
      product_id: string;
      quantity: number;
      unit_price: number;
      products?: { id: string; name: string; base_price?: number };
    }>;
  } | null;
}

export function InvoiceForm({ isOpen, onClose, onSubmit, clients, projects, products, isSubmitting = false, editingInvoice = null }: InvoiceFormProps) {
  const form = useForm<z.infer<typeof invoiceSchema>>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoice_number: "",
      title: "",
      client_id: "",
      project_id: "",
      po_type: "na",
      po_number: "",
      issue_date: new Date(),
      due_date: new Date(),
      status: "not_paid",
      discount_percentage: 0,
      pdf_url: "",
      products: [{ product_id: "", list_price: 0, invoiced_price: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "products"
  });

  // Watch client_id, project_id, issue_date, due_date, status, products, and discount
  const clientId = form.watch("client_id");
  const projectId = form.watch("project_id");
  const issueDate = form.watch("issue_date");
  const dueDate = form.watch("due_date");
  const status = form.watch("status");
  const watchedProducts = form.watch("products");
  const discountPercentage = form.watch("discount_percentage") || 0;

  // Calculate totals
  const subTotal = watchedProducts.reduce((sum, product) => sum + (product.invoiced_price || 0), 0);
  const discountAmount = (subTotal * discountPercentage) / 100;
  const total = subTotal - discountAmount;
  
  useEffect(() => {
    // Reset project selection when client changes
    if (clientId && projectId) {
      const selectedProject = projects.find(p => p.id === projectId);
      if (selectedProject && selectedProject.client_id !== clientId) {
        form.setValue("project_id", "");
      }
    }
  }, [clientId, projectId, projects, form]);

  // Auto-fill invoice title when project is selected
  useEffect(() => {
    if (projectId) {
      const selectedProject = projects.find(p => p.id === projectId);
      if (selectedProject) {
        form.setValue("title", selectedProject.name);
      }
    }
  }, [projectId, projects, form]);

  // Populate form when editing an invoice
  useEffect(() => {
    if (editingInvoice && isOpen) {
      form.setValue("invoice_number", editingInvoice.invoice_number || "");
      form.setValue("title", editingInvoice.title || "");
      form.setValue("client_id", editingInvoice.client_id);
      form.setValue("project_id", editingInvoice.project_id || "");
      form.setValue("issue_date", editingInvoice.issue_date ? new Date(editingInvoice.issue_date) : new Date());
      form.setValue("due_date", editingInvoice.due_date ? new Date(editingInvoice.due_date) : new Date());
      form.setValue("status", editingInvoice.status === "paid" ? "paid" : "not_paid");
      form.setValue("po_number", editingInvoice.po_number || "");
      form.setValue("po_type", editingInvoice.po_type || "na");
      form.setValue("discount_percentage", editingInvoice.discount_percentage || 0);
      form.setValue("pdf_url", editingInvoice.pdf_url || "");
      
      // Handle two cases: 
      // 1. New invoices with invoice_items
      // 2. Legacy invoices with direct amount field
      if (editingInvoice.invoice_items && editingInvoice.invoice_items.length > 0) {
        // New system: use invoice_items
        const products = editingInvoice.invoice_items.map(item => ({
          product_id: item.product_id,
          list_price: item.products?.base_price || 0,
          invoiced_price: item.unit_price,
        }));
        form.setValue("products", products);
        
        // Calculate the correct discount percentage based on actual amounts
        const subtotal = products.reduce((sum, product) => sum + product.invoiced_price, 0);
        const savedAmount = editingInvoice.amount || 0;
        
        if (subtotal > 0 && savedAmount < subtotal) {
          const calculatedDiscount = ((subtotal - savedAmount) / subtotal) * 100;
          form.setValue("discount_percentage", Math.round(calculatedDiscount * 100) / 100);
        } else {
          form.setValue("discount_percentage", editingInvoice.discount_percentage || 0);
        }
      } else if (editingInvoice.amount && editingInvoice.amount > 0) {
        // Legacy system: create a generic product entry with the total amount
        // This allows editing legacy invoices without losing the amount
        const genericProduct = {
          product_id: products?.[0]?.id || "", // Use first available product or empty
          list_price: editingInvoice.amount,
          invoiced_price: editingInvoice.amount,
        };
        form.setValue("products", [genericProduct]);
        form.setValue("discount_percentage", editingInvoice.discount_percentage || 0);
      } else {
        // No amount and no items - empty invoice
        form.setValue("products", [{ product_id: "", list_price: 0, invoiced_price: 0 }]);
      }
    } else if (isOpen && !editingInvoice) {
      // Reset form for new invoice - always clear invoice_number
      form.reset({
        invoice_number: "", // Always start with empty invoice number
        title: "",
        client_id: "",
        project_id: "",
        po_type: "na",
        po_number: "",
        issue_date: new Date(),
        due_date: new Date(),
        status: "not_paid",
        discount_percentage: 0,
        pdf_url: "",
        products: [{ product_id: "", list_price: 0, invoiced_price: 0 }],
      });
    }
  }, [editingInvoice, isOpen, form, products]);

  // Auto-set overdue status - removed since overdue is now calculated for display only

  const handleSubmit = (values: z.infer<typeof invoiceSchema>) => {
    // Calculate total amount with discount applied
    const subtotal = values.products.reduce((sum, product) => sum + product.invoiced_price, 0);
    const discountAmount = (subtotal * (values.discount_percentage || 0)) / 100;
    const totalAmount = subtotal - discountAmount;
    
    // Use the editing invoice's currency if available, otherwise default to EUR
    const currency = editingInvoice?.currency || "EUR";
    
    // Add currency and calculated amount to the submission data
    onSubmit({ ...values, currency, amount: totalAmount });
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingInvoice ? "Edit Invoice" : "Add New Invoice"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Invoice Number and Client */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="invoice_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Number</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., INV-2024-001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="client_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Project, Invoice Title and Purchase Order */}
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="project_id"
                render={({ field }) => {
                  const filteredProjects = projects.filter(p => 
                    clientId ? p.client_id === clientId : false
                  );
                  
                  return (
                    <FormItem>
                      <FormLabel>Project (Optional)</FormLabel>
                      <Select
                        onValueChange={(v) => field.onChange(v === "none" ? "" : v)}
                        value={field.value || undefined}
                        disabled={!clientId}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={
                              clientId 
                                ? filteredProjects.length > 0 
                                  ? "Select project" 
                                  : "No projects available"
                                : "Select client first"
                            } />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No project</SelectItem>
                          {filteredProjects.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
               />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Title</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Brand Study Q4" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="po_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Order (PO)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Type PO number or 'NA'"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            // Update po_type based on input
                            if (value.toLowerCase() === "na" || value === "") {
                              form.setValue("po_type", "na");
                            } else {
                              form.setValue("po_type", "po_number");
                            }
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Issue Date, Due Date, and Status */}
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="issue_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Issue Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}>
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date > new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) || date < new Date("1900-01-01")
                          }
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}>
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const issueDate = form.watch("issue_date");
                            return date < (issueDate || new Date()) || date > new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000);
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="not_paid">Not Paid</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Products Section */}
            <div className="space-y-4">
              <FormLabel className="text-base font-semibold">Products</FormLabel>
              
              {/* Products Header - Only show Product and Invoiced Price columns */}
              <div className="grid grid-cols-2 gap-4 px-4 py-2 bg-gray-50 rounded-t-lg border-b">
                <div className="font-medium text-sm text-gray-700">Product</div>
                <div className="font-medium text-sm text-gray-700">Invoiced Price (€)</div>
              </div>
              
              {fields.map((field, index) => (
                <div key={field.id}>
                  {/* Hidden List Price field */}
                  <input 
                    type="hidden" 
                    {...form.register(`products.${index}.list_price`)}
                  />
                  
                  <div className="grid grid-cols-2 gap-4 items-end p-4 border-x border-b rounded-none">
                    <FormField
                      control={form.control}
                      name={`products.${index}.product_id`}
                      render={({ field }) => (
                        <FormItem>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              // Don't auto-fill prices anymore - prices should come from project value
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select product" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-[300px]">
                              {products.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`products.${index}.invoiced_price`}
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex gap-2 items-end">
                            <FormControl>
                              <Input 
                                type="number" 
                                step="0.01" 
                                min="0"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            {fields.length > 1 && (
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="icon"
                                onClick={() => remove(index)}
                                className="shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                            {index === fields.length - 1 && (
                              <Button 
                                type="button" 
                                size="icon"
                                onClick={() => append({ product_id: "", list_price: 0, invoiced_price: 0 })}
                                className="shrink-0 bg-purple-600 hover:bg-purple-700 text-white border-purple-600 hover:border-purple-700"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}

              {/* Calculation Section */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Sub-Total (€):</span>
                  <span className="font-semibold">{subTotal.toFixed(2)}</span>
                </div>

                <FormField
                  control={form.control}
                  name="discount_percentage"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between items-center gap-4">
                        <FormLabel className="font-medium">Discount (%):</FormLabel>
                        <div className="w-24">
                          <FormControl>
                            <Input 
                              type="number" 
                              step="any" 
                              min="0" 
                              max="100"
                              placeholder="0"
                              value={field.value || 0}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-between items-center text-lg font-bold border-t pt-3">
                  <span>Total (€):</span>
                  <span>{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* PDF Upload Section */}
            <FormField
              control={form.control}
              name="pdf_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invoice PDF (Optional)</FormLabel>
                  <FormControl>
                    <ImageUpload
                      value={field.value || ""}
                      onChange={field.onChange}
                      bucket="invoice-pdfs"
                      className="w-full h-32"
                      label="Upload PDF"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Invoice
              </Button>
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}